import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';

export interface GlobalErrorState {
    isVisible: boolean;
    errorType: 'network' | 'server' | 'auth' | 'unknown';
    statusCode?: number;
    message: string;
    canRetry: boolean;
    retryCallback?: () => void;
}

@Injectable({
    providedIn: 'root',
})
export class GlobalErrorService {
    // Signal-based state management for reactive UI updates
    private readonly _errorState = signal<GlobalErrorState>({
        isVisible: false,
        errorType: 'unknown',
        message: '',
        canRetry: false,
    });

    // Expose read-only signal
    readonly errorState = this._errorState.asReadonly();

    // Store the last failed request for retry functionality
    private _lastFailedRequest: {
        request: HttpRequest<any>;
        next: (req: HttpRequest<any>) => any;
    } | null = null;

    /**
     * Shows the global error screen with the provided error details
     */
    showError(error: HttpErrorResponse, retryCallback?: () => void): void {
        const errorType = this._determineErrorType(error);
        const message = this._getErrorMessage(error, errorType);
        const canRetry = this._canRetryError(error);

        this._errorState.set({
            isVisible: true,
            errorType,
            statusCode: error.status,
            message,
            canRetry,
            retryCallback,
        });
    }

    /**
     * Stores the last failed request for retry functionality
     */
    storeFailedRequest(request: HttpRequest<any>, next: (req: HttpRequest<any>) => any): void {
        this._lastFailedRequest = { request, next };
    }

    /**
     * Hides the global error screen
     */
    hideError(): void {
        this._errorState.set({
            isVisible: false,
            errorType: 'unknown',
            message: '',
            canRetry: false,
        });
        this._lastFailedRequest = null;
    }

    /**
     * Retries the last failed request
     */
    retry(): void {
        const state = this._errorState();

        if (state.retryCallback) {
            // If a custom retry callback is provided, use it
            this.hideError();
            state.retryCallback();
        } else if (this._lastFailedRequest) {
            // Otherwise, retry the last failed HTTP request
            const { request, next } = this._lastFailedRequest;
            this.hideError();
            next(request).subscribe({
                error: (error: HttpErrorResponse) => {
                    // If retry fails, show error again
                    this.showError(error);
                },
            });
        }
    }

    /**
     * Determines the error type based on the HTTP error response
     */
    private _determineErrorType(error: HttpErrorResponse): GlobalErrorState['errorType'] {
        if (error.status === 0 || error.error instanceof ErrorEvent) {
            return 'network';
        }
        if (error.status === 401 || error.status === 403) {
            return 'auth';
        }
        if (error.status >= 500) {
            return 'server';
        }
        return 'unknown';
    }

    /**
     * Gets a user-friendly error message based on error type
     */
    private _getErrorMessage(error: HttpErrorResponse, errorType: GlobalErrorState['errorType']): string {
        switch (errorType) {
            case 'network':
                return 'Unable to connect to the server. Please check your internet connection and try again.';
            case 'auth':
                return 'Your session has expired. Please log in again to continue.';
            case 'server':
                return "We're experiencing technical difficulties. Our team has been notified and is working to resolve the issue.";
            default:
                return 'Something went wrong. Please try again later.';
        }
    }

    /**
     * Determines if the error can be retried
     */
    private _canRetryError(error: HttpErrorResponse): boolean {
        // Network errors and server errors (500+) can be retried
        // Auth errors (401, 403) should not be retried as they require re-authentication
        const errorType = this._determineErrorType(error);
        return errorType === 'network' || errorType === 'server';
    }
}
